import { useEffect } from 'react';
import { useGlobalWebSocket } from '../useGlobalWebSocket';
import { useUserWebSocket } from '../useUserWebSocket';
import { WSMessage, WSMessageType, SocialEvent } from '../../types/websocket';
import { useUserStoreData } from './useUserManager';
import { useAppStore } from '../../stores/appStore';
import { getPendingRequests, getFriends } from '../api/friendshipAPI';
import { getGroupJoinRequests } from '../api/groupJoinRequestAPI';
import { getMyGroups } from '../api/userGroupAPI';
import { MessageType } from '../../model/AppUIShapes';

const STATUS_ACCEPTED = 1;
const STATUS_REJECTED = 2;

function statusText(status: number | undefined): string {
  switch (status) {
    case STATUS_ACCEPTED: return '已接受';
    case STATUS_REJECTED: return '已拒绝';
    default: return '已取消';
  }
}
import { useAnalysisUpdateManager } from './useAnalysisUpdateManager';

export const useGlobalNotificationHandler = () => {
  const { currentUser } = useUserStoreData();
  const { onMessage, isConnected } = useGlobalWebSocket();
  const { onSocialEvent } = useUserWebSocket();
  const addMessage = useAppStore((state) => state.addMessage);
  const setFriends = useAppStore((state) => state.setFriends);
  const setUserGroups = useAppStore((state) => state.setUserGroups);
  const setPendingRequests = useAppStore((state) => state.setPendingRequests);
  const setGroupJoinRequests = useAppStore((state) => state.setGroupJoinRequests);
  const { handleAnalysisUpdate } = useAnalysisUpdateManager();

  const refreshFriendData = async () => {
    try {
      const [friendsResponse, pendingResponse] = await Promise.allSettled([
        getFriends(),
        getPendingRequests()
      ]);
      const friendsData = friendsResponse.status === 'fulfilled' ? friendsResponse.value : [];
      const pendingData = pendingResponse.status === 'fulfilled' ? pendingResponse.value : [];
      if (friendsResponse.status === 'rejected') console.error('[refreshFriendData] getFriends failed:', friendsResponse.reason);
      if (pendingResponse.status === 'rejected') console.error('[refreshFriendData] getPendingRequests failed:', pendingResponse.reason);
      console.info('[refreshFriendData] friends:', Array.isArray(friendsData) ? friendsData.length : 0, 'pendingRequests:', Array.isArray(pendingData) ? pendingData.length : 0);
      setFriends(Array.isArray(friendsData) ? friendsData : []);
      setPendingRequests(Array.isArray(pendingData) ? pendingData : []);
    } catch (error) {
      console.error('刷新好友数据失败:', error);
    }
  };

  const refreshGroupData = async () => {
    try {
      const [groupsResponse, pendingResponse] = await Promise.allSettled([
        getMyGroups(),
        getGroupJoinRequests()
      ]);
      const groupsData = groupsResponse.status === 'fulfilled' ? groupsResponse.value : [];
      const pendingData = pendingResponse.status === 'fulfilled' ? pendingResponse.value : [];
      if (groupsResponse.status === 'rejected') console.error('[refreshGroupData] getMyGroups failed:', groupsResponse.reason);
      if (pendingResponse.status === 'rejected') console.error('[refreshGroupData] getGroupJoinRequests failed:', pendingResponse.reason);
      console.info('[refreshGroupData] groups:', Array.isArray(groupsData) ? groupsData.length : 0, 'pendingJoinRequests:', Array.isArray(pendingData) ? pendingData.length : 0);
      setUserGroups(Array.isArray(groupsData) ? groupsData : []);
      setGroupJoinRequests(Array.isArray(pendingData) ? pendingData : []);
    } catch (error) {
      console.error('刷新群组数据失败:', error);
    }
  };

  const handleSocialEventFromUserWs = (messageType: WSMessageType, event: SocialEvent) => {
    const userInfoMap = useAppStore.getState().userInfoMap;
    const getUserDisplayName = (userId: number | undefined): string => {
      if (!userId) return '未知用户';
      const cached = userInfoMap[String(userId)];
      return cached?.nickName || cached?.username || `User ${userId}`;
    };

    switch (messageType) {
      case WSMessageType.FRIEND_REQUEST: {
        const requesterName = getUserDisplayName(event.actorUserId);
        addMessage(MessageType.INFO, `收到来自 ${requesterName} 的好友请求`);
        refreshFriendData();
        break;
      }
      case WSMessageType.FRIEND_STATUS_CHANGE: {
        const friendName = getUserDisplayName(event.actorUserId);
        const statusTextValue = statusText(event.status);
        addMessage(MessageType.INFO, `${friendName} ${statusTextValue} 好友请求`);
        refreshFriendData();
        break;
      }
      case WSMessageType.GROUP_JOIN_REQUEST: {
        const applicantName = getUserDisplayName(event.actorUserId);
        addMessage(MessageType.INFO, `收到来自 ${applicantName} 的入群申请`);
        refreshGroupData();
        break;
      }
      case WSMessageType.GROUP_JOIN_REQUEST_RESPONSE: {
        const statusTextValue = event.status === STATUS_ACCEPTED ? '已接受' : '已拒绝';
        addMessage(MessageType.INFO, `您的入群申请${statusTextValue}`);
        refreshGroupData();
        break;
      }
    }
  };

  // 监听 user-server WebSocket 的社交事件
  useEffect(() => {
    if (!currentUser?.userId) {
      return;
    }

    const unsubscribe = onSocialEvent(handleSocialEventFromUserWs);
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser?.userId, onSocialEvent, addMessage, setFriends, setUserGroups, setPendingRequests, setGroupJoinRequests]);

  // 监听 IM WebSocket 的社交事件（兼容旧路径）
  useEffect(() => {
    if (!currentUser?.userId) {
      return;
    }

    if (!isConnected()) {
      return;
    }

    const unsubscribe = onMessage((message: WSMessage) => {
      const userInfoMap = useAppStore.getState().userInfoMap;
      const getUserDisplayName = (userId: number | undefined): string => {
        if (!userId) return '未知用户';
        const cached = userInfoMap[String(userId)];
        return cached?.nickName || cached?.username || `User ${userId}`;
      };

      switch (message.messageType) {
        case WSMessageType.FRIEND_REQUEST: {
          const data = message.messageContent;
          const requesterName = getUserDisplayName(data.actorUserId);
          addMessage(MessageType.INFO, `收到来自 ${requesterName} 的好友请求`);
          refreshFriendData();
          break;
        }

        case WSMessageType.FRIEND_STATUS_CHANGE: {
          const data = message.messageContent;
          const friendName = getUserDisplayName(data.actorUserId);
          const statusTextValue = statusText(data.status);
          addMessage(MessageType.INFO, `${friendName} ${statusTextValue} 好友请求`);
          refreshFriendData();
          break;
        }

        case WSMessageType.GROUP_JOIN_REQUEST: {
          const data = message.messageContent;
          const applicantName = getUserDisplayName(data.actorUserId);
          addMessage(MessageType.INFO, `收到来自 ${applicantName} 的入群申请`);
          refreshGroupData();
          break;
        }

        case WSMessageType.GROUP_JOIN_REQUEST_RESPONSE: {
          const data = message.messageContent;
          const statusTextValue = data.status === STATUS_ACCEPTED ? '已接受' : '已拒绝';
          addMessage(MessageType.INFO, `您的入群申请${statusTextValue}`);
          refreshGroupData();
          break;
        }

        case WSMessageType.ANALYSIS_UPDATE: {
          const data = message.messageContent;
          if (data) {
            handleAnalysisUpdate(data);
          }
          break;
        }

        default:
          break;
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser?.userId, isConnected, onMessage, addMessage, setFriends, setUserGroups, setPendingRequests, setGroupJoinRequests, handleAnalysisUpdate]);
};
